import { Component } from '@theme/component';
import { LoadManager } from '@theme/custom-collection-utility';

const Utils = /** @type {NonNullable<typeof window.ThuysversSurveyFormUtils>} */ (window.ThuysversSurveyFormUtils);
const Klaviyo = /** @type {NonNullable<typeof window.ThuysversSurveyFormKlaviyo>} */ (window.ThuysversSurveyFormKlaviyo);
const EMAIL_REGEX = Utils.EMAIL_REGEX;
const STORAGE_KEY_SURVEY_STARTED_EMAIL = 'thuysvers_ecomm_survey_started_email';
const STORAGE_KEY_SURVEY_COMPLETED_EMAIL = 'thuysvers_ecomm_survey_completed_email';

/** data-fieldType values for step routing */
const FIELD_TYPE = {
  SUBMIT: 'Submit',
  EMAIL: 'Email',
  SURVEY_STATUS: 'SurveyStatus',
};

/** data-answerKey for add-ons step (ecomm flow) */
const ANSWER_KEY_ADDONS = 'addons';
const ANSWER_KEY_PROFILE_COUNT = 'profilecount';

/**
 * A custom element that manages the Thuysvers Survey Forms.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} closeSurveyButton - The close survey button element
 * @property {HTMLButtonElement} startSurveyButton - The start survey button element
 * @property {HTMLButtonElement} prevSurveyButton - The previous survey button element
 * @property {HTMLButtonElement} nextSurveyButton - The next survey button element
 * @property {HTMLElement[]} surveyStep[] - The survey step elements
 * @property {HTMLElement} surveyFooterContentWrapper - The survey footer content wrapper element
 * @property {HTMLButtonElement[]} qtyBtnInc[] - Quantity increment buttons
 * @property {HTMLButtonElement[]} qtyBtnDec[] - Quantity decrement buttons
 * @property {HTMLInputElement[]} qtyInput[] - Quantity inputs
 * @property {HTMLElement} surveySubmitWrapper - The survey submit wrapper element
 * @extends {Component<Refs>}
 */
export class ThuysversSurveyFormComponent extends Component {
  /** @type {HTMLFormElement | null} */
  formEl = null;

  /** @type {boolean} */
  isSubmitting = false;

  /** @type {boolean} */
  isCheckingStatus = false;

  /** @type {number} */
  profileCountSelected = 1;

  /** @type {Map<HTMLElement, (event: Event) => void>} */
  qtyBtnHandlers = new Map();

  /** @type {Map<HTMLInputElement, (event: Event) => void>} */
  qtyInputHandlers = new Map();

  // ----- Lifecycle -----

  connectedCallback() {
    try {
      super.connectedCallback();
      this.refs.closeSurveyButton?.addEventListener('click', this.#closeSurveyForm);
      this.refs.startSurveyButton?.addEventListener('click', this.#startSurvey);
      this.refs.prevSurveyButton?.addEventListener('click', this.#prevSurveyStep);
      this.refs.nextSurveyButton?.addEventListener('click', this.#nextSurveyStep);

      this.formEl = this.querySelector('form[data-thuysvers-survey]');
      this.formEl?.addEventListener('input', this.#onFormInputOrChange, true);
      this.formEl?.addEventListener('change', this.#onFormInputOrChange, true);
      this.formEl?.addEventListener('keydown', this.#onFormKeyDown, true);

      this.bindQtyControlsStatic();

      // Initial state (important if the active step is already an email step)
      this.updateNavState();

      // If URL contains a linked recommendations email, hydrate recommendations payload.
      void this.#getRecommendationEmailFromUrl();
    } catch (error) {
      console.error('[THUYSVERS-SURVEY-FORMS] Error in connectedCallback', error);
      throw error;
    }
  }

  disconnectedCallback() {
    // Remove listeners we added in connectedCallback
    this.refs.closeSurveyButton?.removeEventListener('click', this.#closeSurveyForm);
    this.refs.startSurveyButton?.removeEventListener('click', this.#startSurvey);
    this.refs.prevSurveyButton?.removeEventListener('click', this.#prevSurveyStep);
    this.refs.nextSurveyButton?.removeEventListener('click', this.#nextSurveyStep);

    this.formEl?.removeEventListener('input', this.#onFormInputOrChange, true);
    this.formEl?.removeEventListener('change', this.#onFormInputOrChange, true);
    this.formEl?.removeEventListener('keydown', this.#onFormKeyDown, true);
    this.formEl = null;

    // Remove qty listeners we bound in bindQtyControlsStatic
    for (const [btn, handler] of this.qtyBtnHandlers.entries()) {
      btn.removeEventListener('click', handler);
    }
    this.qtyBtnHandlers.clear();

    for (const [input, handler] of this.qtyInputHandlers.entries()) {
      input.removeEventListener('input', handler);
      input.removeEventListener('change', handler);
    }
    this.qtyInputHandlers.clear();

    super.disconnectedCallback();
  }

  // ----- Navigation & steps -----

  getActiveStep() {
    return Utils.getActiveStep(this);
  }

  getActiveStepIndex() {
    return Utils.getActiveStepIndex(this);
  }

  /** @param {number} index */
  setActiveStepIndex(index) {
    return Utils.setActiveStepIndex(this, index);
  }

  updateNavState() {
    Utils.updateNavState(this);
  }

  /** @param {HTMLInputElement} emailInput @returns {boolean} */
  syncEmailValidity(emailInput) {
    return Utils.syncEmailValidity(emailInput);
  }

  canAdvanceFromActiveStep() {
    return Utils.canAdvanceFromActiveStep(this);
  }

  /** @param {Event} event */
  #onFormInputOrChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const activeStep = this.getActiveStep();
    if (!activeStep) return;
    if (!activeStep.contains(target)) return;

    if (target instanceof HTMLInputElement && target.type === 'number' && target.classList.contains('thuysvers-survey-form-qty-input')) return;

    this.updateNavState();
  };

  // ----- API / Klaviyo -----

  getGadgetProgressEndpoint() {
    return Utils.getGadgetProgressEndpoint(this);
  }

  getGadgetStatusEndpoint() {
    return Utils.getGadgetStatusEndpoint(this);
  }

  getCustomerGidFromForm() {
    return Utils.getCustomerGidFromForm(this);
  }

  getSubmitterEmailFromForm() {
    return Utils.getSubmitterEmailFromForm(this);
  }

  /**
   * If the URL has `?recommendation_email=...`, fetch the survey status and persist the
   * recommendations payload to sessionStorage. This mimics the Email-step behavior
   * around the status check + payload persist (see fetchSurveyStatus + persistRecommendationsPayload).
   */
  #getRecommendationEmailFromUrl = async () => {
    /** @type {boolean} */
    let loaderShown = false;
    try {
      if (/** @type {any} */ (window).themeSettings?.enableEcommCollectionRecommendations === false) return;
      const search = window.location?.search || '';
      if (!search) return;

      const params = new URLSearchParams(search);
      const raw = params.get('recommendation_email');
      if (!raw) return;

      // URLSearchParams treats "+" as space; restore it for emails.
      const email = String(raw).trim().replace(/\s+/g, '+');
      if (!email || !EMAIL_REGEX.test(email)) return;

      LoadManager.addLoader();
      loaderShown = true;

      const status = await this.fetchSurveyStatus(email);
      if (status.hasTakenSurvey) {
        // Mimic successful submit side-effect: persist payload (so recommendations/quick-filters update).
        if (status.payload) {
          this.persistRecommendationsPayload(status.payload);
        }
      }
      else {
        // If the email has NOT taken the survey, ensure "Aanbevolen" isn't applied.
        // Reuse facets logic by removing the recommendations URL param and letting
        // `facets-form-component.updateFiltersByURLWithoutBrowserUpdate()` re-render + sync quick filters.
        const RECO_PARAM = 'filter.p.m.custom.product_recommendations';
        const url = new URL(window.location.href);
        if (url.searchParams.has(RECO_PARAM)) {
          url.searchParams.delete(RECO_PARAM);

          const facetsFormComponent = document.querySelector('#filters-drawer facets-form-component');
          if (
            facetsFormComponent &&
            typeof /** @type {any} */ (facetsFormComponent).updateFiltersByURLWithoutBrowserUpdate === 'function'
          ) {
            await /** @type {any} */ (facetsFormComponent).updateFiltersByURLWithoutBrowserUpdate(url.toString());
          } else if (
            facetsFormComponent &&
            typeof /** @type {any} */ (facetsFormComponent).updateFiltersByURL === 'function'
          ) {
            // Fallback: this pushes a history entry, but still reuses the same facets section-render path.
            await /** @type {any} */ (facetsFormComponent).updateFiltersByURL(url.toString());
          } else {
            // Last-resort: update the visible URL (no history entry) and ask facets to refresh.
            try {
              history.replaceState(history.state, '', url.toString());
            } catch {
              // ignore
            }
            if (facetsFormComponent && typeof /** @type {any} */ (facetsFormComponent).updateFilters === 'function') {
              await /** @type {any} */ (facetsFormComponent).updateFilters();
            }
          }
        }
      }
    } catch (error) {
      console.warn('[THUYSVERS-SURVEY] Failed to hydrate recommendations from recommendation_email param', error);
    } finally {
      if (loaderShown) {
        try {
          LoadManager.removeLoader();
        } catch {
          // ignore
        }
      }
    }
  }

  /** @param {'started'|'completed'} status @param {string} email @param {Record<string, unknown>} [extra] */
  async sendSurveyProgress(status, email, extra) {
    return Klaviyo ? Klaviyo.sendSurveyProgress(this, status, email, extra) : Promise.resolve();
  }

  async maybeTrackSurveyStarted() {
    return Klaviyo ? Klaviyo.maybeTrackSurveyStarted(this, {
      storageKeyStarted: STORAGE_KEY_SURVEY_STARTED_EMAIL,
      storageKeyCompleted: STORAGE_KEY_SURVEY_COMPLETED_EMAIL,
      emailRegex: EMAIL_REGEX,
    }) : Promise.resolve();
  }

  /** @param {string} email */
  async fetchSurveyStatus(email) {
    return Utils.fetchSurveyStatus(this, email, { emailRegex: EMAIL_REGEX });
  }

  getSurveyStatusStepIndex() {
    return Utils.getSurveyStatusStepIndex(this);
  }

  /** @param {boolean} enabled */
  setSurveyStatusStepEnabled(enabled) {
    return Utils.setSurveyStatusStepEnabled(this, enabled);
  }

  // ----- Quantity controls -----

  bindQtyControlsStatic() {
    Utils.bindQtyControlsStatic(this);
  }

  /** @param {HTMLInputElement} input @returns {number} */
  getQtyInputNumber(input) {
    return Utils.getQtyInputNumber(input);
  }

  /** @param {HTMLInputElement} input */
  reportQtyRangeValidity(input) {
    return Utils.reportQtyRangeValidity(input);
  }

  /** @param {HTMLInputElement} input @param {number} delta */
  bumpQtyInputValue(input, delta) {
    return Utils.bumpQtyInputValue(this, input, delta);
  }

  // ----- Step handlers (Prev/Next/Start) -----

  /** @param {KeyboardEvent} event */
  #onFormKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    const target = event.target;
    if (target instanceof HTMLTextAreaElement) return;

    event.preventDefault();

    if (this.canAdvanceFromActiveStep()) {
      this.#nextSurveyStep();
      return;
    }

    const activeStep = this.getActiveStep();
    const emailInput = activeStep?.querySelector('input[type="email"]');
    if (emailInput instanceof HTMLInputElement) emailInput.reportValidity();
  };

  #startSurvey = () => {
    this.setActiveStepIndex(1);
    this.updateNavState();
  };

  #prevSurveyStep = () => {
    const currentStep = this.getActiveStep();
    const currentIndex = this.getActiveStepIndex();
    if (!currentStep || currentIndex <= 0) return;

    const answerKey = String(currentStep.dataset?.answerKey || '').trim().toLowerCase();
    if (answerKey === ANSWER_KEY_ADDONS) {
      this.profileCountSelected = this.getSelectedProfileCount();
      this.applyPersonStepSkipping(this.profileCountSelected);

      const targetIndex = this.findLastPersonStepIndex(this.profileCountSelected);
      if (targetIndex >= 0) {
        this.setActiveStepIndex(targetIndex);
        this.updateNavState();
        return;
      }
    }

    const prevIndex = this.findNextVisibleStepIndex(currentIndex, -1);
    if (prevIndex < 0) return;
    this.setActiveStepIndex(prevIndex);
    this.updateNavState();
  };

  #nextSurveyStep = async () => {
    // Phase 0: Block if validation fails
    if (!this.canAdvanceFromActiveStep()) {
      const activeStep = this.getActiveStep();
      const emailInput = activeStep?.querySelector('input[type="email"]');
      if (emailInput instanceof HTMLInputElement) emailInput.reportValidity();
      return;
    }

    const activeStep = this.getActiveStep();

    // Phase 1: SurveyStatus guard → close & redirect
    if (activeStep?.dataset?.fieldType === FIELD_TYPE.SURVEY_STATUS) {
      this.formEl?.reset();
      this.setSurveyStatusStepEnabled(false);
      this.setActiveStepIndex(0);
      this.updateNavState();
      this.#closeSurveyForm();
      const redirectUrl = this.formEl?.getAttribute('data-redirect-url');
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
      return;
    }

    // Phase 2: Email step → check status API or track "started"
    if (activeStep?.dataset?.fieldType === FIELD_TYPE.EMAIL) {
      if (this.isCheckingStatus) return;
      this.isCheckingStatus = true;
      this.refs.nextSurveyButton && (this.refs.nextSurveyButton.disabled = true);

      try {
        const email = this.getSubmitterEmailFromForm();
        const status = await this.fetchSurveyStatus(email);
        if (status.hasTakenSurvey) {
          if (status.payload) {
            Utils.persistRecommendationsPayload(status.payload);
          }
          this.setSurveyStatusStepEnabled(true);
          const idx = this.getSurveyStatusStepIndex();
          if (idx >= 0) {
            this.setActiveStepIndex(idx);
            this.updateNavState();
            return;
          }
        } else {
          await this.maybeTrackSurveyStarted();
        }
      } finally {
        this.isCheckingStatus = false;
      }
    }

    // Phase 3: ProfileCount step → apply person-step skipping
    const activeAnswerKey = String(activeStep?.dataset?.answerKey || '').trim().toLowerCase();
    if (activeAnswerKey === ANSWER_KEY_PROFILE_COUNT) {
      this.profileCountSelected = this.getSelectedProfileCount();
      this.applyPersonStepSkipping(this.profileCountSelected);
    }

    const currentIndex = this.getActiveStepIndex();
    if (currentIndex < 0) return;

    // Phase 4: Submit step (from URL/back) → close & redirect
    if (activeStep?.dataset?.fieldType === FIELD_TYPE.SUBMIT) {
      this.formEl?.reset();
      this.setActiveStepIndex(0);
      this.updateNavState();
      this.#closeSurveyForm();
      const redirectUrl = this.formEl?.getAttribute('data-redirect-url');
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
      return;
    }

    // Phase 5: Advance to next step
    const nextIndex = this.findNextVisibleStepIndex(currentIndex, 1);
    const nextStep = nextIndex >= 0 ? this.refs.surveyStep?.[nextIndex] : null;
    if (!nextStep) return;

    this.setActiveStepIndex(nextIndex);

    // Phase 6: If next step is Submit → run async submit flow
    if (nextStep.dataset?.fieldType === FIELD_TYPE.SUBMIT) {
      const payload = /** @type {Record<string, unknown>} */ (Utils.buildSubmissionJson(this));
      Utils.runSubmitStepAsync(this, payload, {
        /** @param {Record<string, unknown>} p @param {() => string} getForm */
        getCompletedEmail: (p, getForm) =>
          (p?.submitter && typeof p.submitter === 'object' && typeof /** @type {{ email?: string }} */ (p.submitter).email === 'string' &&
            /** @type {{ email?: string }} */ (p.submitter).email.trim())
            ? /** @type {{ email?: string }} */ (p.submitter).email.trim()
            : getForm(),
        storageKeyCompleted: STORAGE_KEY_SURVEY_COMPLETED_EMAIL,
        emailRegex: EMAIL_REGEX,
        /** @param {string} email @param {Record<string, unknown>} [extra] */
        sendProgress: Klaviyo ? (email, extra) => void Klaviyo.sendSurveyProgress(this, 'completed', email, extra) : undefined,
      });
    }

    this.updateNavState();
  };

  /** @param {number | undefined} status @returns {string} */
  getSubmitErrorMessage(status) {
    return Utils.getSubmitErrorMessage(status);
  }

  /** @param {unknown} payload @returns {Promise<{ ok: boolean; status?: number }>} */
  async submitToGadgetEndpoint(payload) {
    return Utils.submitToGadgetEndpoint(this, payload);
  }

  /** @param {unknown} payload */
  persistRecommendationsPayload(payload) {
    return Utils.persistRecommendationsPayload(payload);
  }

  /** @param {unknown} payload */
  persistChurnedRecommendationsPayload(payload) {
    return Utils.persistChurnedRecommendationsPayload(payload);
  }

  // ----- Utils wrappers -----

  getSelectedProfileCount() {
    return Utils.getSelectedProfileCount(this);
  }

  /** @param {number} profileCount */
  applyPersonStepSkipping(profileCount) {
    return Utils.applyPersonStepSkipping(this, profileCount);
  }

  /** @param {number} fromIndex @param {1 | -1} direction @returns {number} */
  findNextVisibleStepIndex(fromIndex, direction) {
    return Utils.findNextVisibleStepIndex(this, fromIndex, direction);
  }

  /** @param {number} profileCount @returns {number} */
  findLastPersonStepIndex(profileCount) {
    return Utils.findLastPersonStepIndex(this, profileCount);
  }


  /** @param {HTMLFormElement} form @returns {unknown[]} */
  collectAddOns(form) {
    return Utils.collectAddOns(this, form);
  }

  /** @param {string} raw @returns {{ size: string, unit: string }} */
  parseSizeUnit(raw) {
    return Utils.parseSizeUnit(raw);
  }

  showSurveyForm = () => {
    Utils.showSurveyForm(this);
  };

  #closeSurveyForm = () => {
    Utils.closeSurveyForm(this);
  };
}

if (!customElements.get('thuysvers-survey-form-component')) {
  customElements.define('thuysvers-survey-form-component', ThuysversSurveyFormComponent);
}
